import * as _ from 'underscore';

import { FlowBasicInfo, FlowDetailInfo, FlowListItemInfo } from '../models/FlowInfo';
import { IUserInfo } from '../models/IUserInfo';
import { INamedItemInfo } from '../models/resourceInfoParts';
import { FlowBasic, FlowDetail, FlowListItem } from '../ServerApi/types/Flow';
import { FlowDraft, ContentDraftStatus } from '../ServerApi/types/ContentDraft';
import { IUsageInfo } from '../ServerApi/types/IUsageInfo';
import { ContentMapper } from './ContentMapper';
import { MapperHelper } from './MapperHelper';
import { ProjectMapper } from './ProjectMapper';
import { UserMapper } from './UserMapper';
import { appendDraftIdPrefix } from '../../drafts/utils/DraftUtils';
import { FeatureFlags } from '../FeatureFlags';

export module FlowMapper {

  const M = MapperHelper;

  export function mapBasic(
    serverApiFlows: ReadonlyArray<FlowBasic>,
    users: _.Dictionary<IUserInfo>,
    projects: _.Dictionary<INamedItemInfo> = {},
  ): FlowBasicInfo[] {
    const flowsToProject = ContentMapper.getContentToProjectMap(ContentMapper.ContentTypeToTargetMap.flow, projects);
    return _.map(serverApiFlows, (f: FlowBasic) => {
      const fbInfo: FlowBasicInfo = {
        id:                        f.id,
        luid:                      f.luid,
        name:                      f.name,
        owner:                     users[f.ownerId],
        project:                   (FeatureFlags.refactorProjectEnabled) ? flowsToProject[f.id] : projects[f.projectId],
        contentType:               'flow',
      };
      return fbInfo;
    });
  }

  export function map(
    flows: FlowListItem[],
    users: _.Dictionary<IUserInfo>,
    projects: _.Dictionary<INamedItemInfo>,
    usageInfoMaxValues?: IUsageInfo,
    favoriteFlows: _.Dictionary<any> = {},
  ): FlowListItemInfo[] {
    const flowsToProject = ContentMapper.getContentToProjectMap(ContentMapper.ContentTypeToTargetMap.flow, projects);
    return _.map(flows, (f: FlowListItem) =>
      mapOne(f, users[f.ownerId], (FeatureFlags.refactorProjectEnabled) ? flowsToProject[f.id] : projects[f.projectId],
        usageInfoMaxValues, !!favoriteFlows[f.id])
    );
  }

  export function mapOneForAlert(
    f: FlowBasic,
  ): FlowBasicInfo {
    return {
      id: f.id,
      luid: f.luid,
      name: f.name,
      contentType: 'flow',
    };
  }

  export function mapOne(
    f: FlowListItem,
    owner: IUserInfo,
    project: INamedItemInfo,
    usageInfoMaxValues?: IUsageInfo,
    favorite?: boolean
  ): FlowListItemInfo {
    return {
      id:                    f.id ? f.id : appendDraftIdPrefix(f.draftId), // drafts with 'unpublished' status don't have an id so map to draftId with prefix for uniqueness
      luid:                  f.luid,
      inputCount:            f.inputCount,
      name:                  f.name,
      outputCount:           f.outputCount,
      owner:                 UserMapper.mapOne(owner),
      project:               project,
      description:           f.description,
      updatedAt:             M.toDate(f.updatedAt),
      favorite:              favorite,
      tags:                  M.sortTags(f.tags),
      usageInfo:             f.usageInfo,
      usageInfoMaxValues:    usageInfoMaxValues || <IUsageInfo>{},
      contentType:           'flow',
      flowFileType:          f.fileType,
      flowDocumentVersion:   f.documentVersion,
      thumbnailUrl:          f.thumbnailUrl,
      dataQualityIndicators: f.dataQualityIndicators || [],
      draftStatus:           f.draftStatus,
      draftId:               f.draftId ? f.draftId : null,
      draftLuid:             f.draftLuid ? f.draftLuid : null,
    };
  }

  export function mapDetail(f: FlowDetail): FlowDetailInfo {
    return {
      id:                    f.id ? f.id : appendDraftIdPrefix(f.draftId), /// drafts with 'unpublished' status don't have an id so map to draftId with prefix for uniqueness
      inputCount:            f.inputCount,
      name:                  f.name,
      outputCount:           f.outputCount,
      createdAt:             M.toDate(f.createdAt),
      description:           f.description,
      updatedAt:             M.toDate(f.updatedAt),
      downloadUrl:           f.downloadUrl,
      editUrl:               f.editUrl,
      favorite:              f.favorite,
      tags:                  M.sortTags(f.tags),
      owner:                 UserMapper.mapOne(f.owner),
      project:               ProjectMapper.mapName(f.project),
      contentType:           'flow',
      flowGraphImageUrl:     f.flowGraphImageUrl,
      flowFileType:          f.fileType,
      flowDocumentVersion:   f.documentVersion,
      hasExtracts:           f.hasExtracts,
      hasEncryptableExtracts: f.hasEncryptableExtracts,
      extractEncryptionState: f.extractEncryptionState,
      dataQualityIndicators:  f.dataQualityIndicators || [],
      luid:                   f.luid,
      draftStatus:            f.draftStatus,
      flowDraft:              f.flowDraft,
      draftId:                f.draftId ? f.draftId : null,
      draftLuid:              f.draftLuid ? f.draftLuid : null
    };
  }

  export function mapFromCRASS(content: any): FlowListItemInfo{
    return {
        id:                    content.id + '',
        luid:                  content.luid,
        inputCount:            0, //missing
        name:                  content.title,
        outputCount:           0, //missing
        project:               null, //missing
        description:           content.description,
        updatedAt:             M.toDate(content.modifiedTime) || M.toDateFromTimeOffsetObject(content.modifiedTime),
        favorite:              false, //missing
        tags:                  M.sortTags(content.tags),
        usageInfo:             null, //missing
        usageInfoMaxValues:    <IUsageInfo>{}, //missing
        contentType:           'flow',
        flowFileType:          content.fileType,
        flowDocumentVersion:   content.version,
        thumbnailUrl:          getFlowThumbnailUrl(content.id, (M.toDate(content.modifiedTime) || M.toDateFromTimeOffsetObject(content.modifiedTime))),
        dataQualityIndicators: [], //missing
        owner: {
            id: content.ownerId + '',
            displayName: content.ownerName || '',
            username: content.ownerAlias || '',
            domainName: content.ownerDomain,
            isHiddenInternalUser: false
        },
        draftStatus:           content.draftStatus,
        draftId:               null, // TODO
        draftLuid:             null, // TODO
      };
  }

  function getFlowThumbnailUrl(flowId: String, updatedAt: Date){
    const cacheValue = updatedAt ? updatedAt.getTime() : '';
    return M.REST_API_BASE_URL + `flows/${flowId}/thumbnail?${cacheValue}`;
  }
}
